import React, { useState, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Search, Loader2, X } from 'lucide-react';

const LocationSearch = ({ value, onChange, onSelect }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  const searchLocation = useCallback(async (term) => {
    if (term.length < 3) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(term)}&countrycodes=in&limit=5`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      setResults(data);
      setShowDropdown(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange?.(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchLocation(val), 400);
  };

  const handleSelect = (item) => {
    setQuery(item.display_name);
    onChange?.(item.display_name);
    onSelect?.({
      address: item.display_name,
      latitude: parseFloat(item.lat),
      longitude: parseFloat(item.lon),
    });
    setShowDropdown(false);
    setResults([]);
  };

  const clearInput = () => {
    setQuery('');
    onChange?.('');
    onSelect?.({ address: '', latitude: null, longitude: null });
    setResults([]);
    setShowDropdown(false);
  };

  return (
    <div className="relative" data-testid="location-search">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder="Search location (e.g., Koramangala, Bangalore)"
          className="pl-10 pr-10"
          data-testid="location-search-input"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
        {query && !loading && (
          <button onClick={clearInput} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
          </button>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white rounded-lg border shadow-lg max-h-60 overflow-y-auto">
          {results.map((item, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full text-left px-4 py-3 hover:bg-indigo-50 flex items-start gap-3 border-b last:border-b-0 transition-colors"
              data-testid={`location-result-${idx}`}
            >
              <MapPin className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-slate-700 line-clamp-2">{item.display_name}</span>
            </button>
          ))}
        </div>
      )}

      {showDropdown && query.length >= 3 && results.length === 0 && !loading && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white rounded-lg border shadow-lg p-4">
          <p className="text-sm text-slate-500 text-center">No locations found</p>
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
